import { Component, effect, input, output, signal } from '@angular/core';
import { Router } from '@angular/router';
import { AppConfig } from '../shared/app.config';
import moment from 'moment';

interface MemberName {
  first?: string;
  last?: string;
}

interface MemberSelection {
  name?: MemberName;
  dependentType?: { value?: string };
  planAcronym?: string;
  clientSpecifiedIdentifier?: string;
  clientSpecifiedId?: string;
  clientId?: string;
  client?: { specifiedId?: string };
  subclientSpecifiedIdentifier?: string;
  subClientSpecifiedIdentifier?: string;
  subclientSpecifiedId?: string;
  subClientSpecifiedId?: string;
  subClientId?: string;
  specifiedId?: string;
  subscriberEnrollmentId?: string | number;
  subscriberEnrollmentID?: string | number;
  enrollmentId?: string | number;
  eligibilityEffectiveDate?: string;
  asOf?: string | null;
  firstName?: string;
  lastName?: string;
}

interface MemberSelectionEvent {
  value?: MemberSelection;
}

interface ClientSelected {
  planAcronym: string;
  clientSpecifiedId: string;
  subClientSpecifiedId: string;
  asOf?: string | null;
}

interface SubscriberFeed {
  enrollmentId: number;
  asOf?: string | null;
}

interface AddMemberFeed {
  subscriberName: string;
  planAcronym: string;
  clientId: string;
  subClientId: string;
  memberId: string;
  subscriberEnrollmentID?: string | number;
  foundMember?: any;
  asOf: string;
  adminPlan: string;
}

export interface MemberStorePayload {
  [key: string]: unknown;
  event: 'add flow';
  selectedSubscriber: MemberSelection;
  addMemberFeed: AddMemberFeed;
  selectedDependent: Record<string, unknown>;
  dependentAdded: boolean;
}

interface ClientDetailsWithAgeLimit {
  client?: {
    subClient?: {
      contract?: {
        eligAgeLimitsConfig?: {
          child?: {
            maxAgeLimit?: {
              limit?: number;
            };
          };
        };
      };
    };
  };
}

@Component({
    selector: 'app-member-details-page',
    templateUrl: './member-details.component.html',
    styleUrls: ['./member-details.component.scss'],
    standalone: false
})
export class MemberDetailsComponent {
  readonly region: string;
  readonly disableSwitchMember = signal(false);
  readonly initialMemberSelected = input<MemberSelection>({ name: { first: '', last: '' } });
  readonly memberSelected = signal<MemberSelection>({ name: { first: '', last: '' } });
  readonly initialClientSelected = input<ClientSelected>({ planAcronym: '', clientSpecifiedId: '', subClientSpecifiedId: '' });
  readonly clientSelected = signal<ClientSelected>({ planAcronym: '', clientSpecifiedId: '', subClientSpecifiedId: '' });
  readonly actionSelected = signal('');
  readonly transferVisible = signal('');
  readonly showBackToResults = input(false);
  readonly clientDetails = signal<ClientDetailsWithAgeLimit | null>(null);
  readonly productDetails = signal<unknown>(null);
  readonly selectedAdminPlan = input('');
  readonly subscriberFeed = input<SubscriberFeed | undefined>(undefined);
  readonly playerUnderVsp = ['DDPMI', 'DDPOH', 'DDPIN'];
  setMemberStore = output<MemberStorePayload>();
  private lastClientContextKey = '';

  constructor(
    private readonly route: Router,
    private readonly config: AppConfig
  ) {
    this.region = this.config.region;

    effect(() => {
      this.memberSelected.set(this.initialMemberSelected());
    });

    effect(() => {
      const nextClientSelected = this.initialClientSelected();
      const nextClientContextKey = this.buildClientContextKey(nextClientSelected);
      if (nextClientContextKey !== this.lastClientContextKey) {
        this.lastClientContextKey = nextClientContextKey;
        this.clientDetails.set(null);
        this.productDetails.set(null);
      }

      this.clientSelected.set(nextClientSelected);
    });
  }

  goToMemberSearch(): void {
    this.route.navigate(['eligibility/member']);
  }

  scroll(id: string): void {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView();
    }
  }

  onClientInfo(details: unknown): void {
    this.clientDetails.set(details);
  }

  onPlanDetailsInfo(details: unknown): void {
    this.productDetails.set(details);
  }

  getClientAgeLimit(): number {
    return this.clientDetails()?.client?.subClient?.contract?.eligAgeLimitsConfig?.child?.maxAgeLimit?.limit ?? 0;
  }

  headerSelectedFromFamilySection(event: string | MemberSelection | { subscriber?: MemberSelection } | null): void {
    if (typeof event === 'string' || !event) {
      return;
    }

    const subscriber: MemberSelection = (event as { subscriber?: MemberSelection }).subscriber ?? (event as MemberSelection);
    const clientSelected = this.clientSelected();
    const firstName = subscriber?.firstName || subscriber?.name?.first || '';
    const lastName = subscriber?.lastName || subscriber?.name?.last || '';
    const planAcronym = subscriber?.planAcronym || clientSelected?.planAcronym;
    const clientId = subscriber?.clientSpecifiedIdentifier
      || subscriber?.clientSpecifiedId
      || subscriber?.clientId
      || subscriber?.client?.specifiedId
      || clientSelected?.clientSpecifiedId;
    const subClientId = subscriber?.subclientSpecifiedIdentifier
      || subscriber?.subClientSpecifiedIdentifier
      || subscriber?.subclientSpecifiedId
      || subscriber?.subClientSpecifiedId
      || subscriber?.subClientId
      || subscriber?.specifiedId
      || clientSelected?.subClientSpecifiedId;
    const subscriberEnrollmentID = subscriber?.subscriberEnrollmentId
      || subscriber?.subscriberEnrollmentID
      || subscriber?.enrollmentId
      || this.subscriberFeed()?.enrollmentId;
    const eligibilityEffectiveDate = subscriber?.eligibilityEffectiveDate;
    const candidateAsOf = subscriber?.asOf || this.subscriberFeed()?.asOf;
    const resolvedAsOf = eligibilityEffectiveDate
      && moment(eligibilityEffectiveDate).isAfter(moment(candidateAsOf || new Date()).format('YYYY-MM-DD'))
      ? eligibilityEffectiveDate
      : (candidateAsOf || '');
    if (!planAcronym || !clientId || !subClientId) {
      return;
    }

    this.setMemberStore.emit({
      event: 'add flow',
      selectedSubscriber: {
        ...subscriber,
        firstName,
        lastName,
        planAcronym,
        clientSpecifiedIdentifier: clientId,
        subclientSpecifiedIdentifier: subClientId,
        subscriberEnrollmentId: subscriberEnrollmentID,
        subscriberEnrollmentID,
        asOf: resolvedAsOf
      },
      addMemberFeed: {
        subscriberName: `${firstName} ${lastName}`.trim(),
        planAcronym,
        clientId,
        subClientId,
        memberId: '',
        subscriberEnrollmentID,
        asOf: resolvedAsOf,
        adminPlan: this.selectedAdminPlan()
      },
      selectedDependent: {},
      dependentAdded: false
    });
  }

  headerSelectedFromMemberInfo(event: string): void {
    const CONDITION_TO_NOT_CHECK = ['success', 'cancelled', 'terminate', 'reinstate', 'transfer', 'copy'];
    // look for 'edit', 'success', 'cancelled'
    this.disableSwitchMember.set(event === 'edit' || CONDITION_TO_NOT_CHECK.indexOf(event) <= -1);
    // To show the terminate/reinstate flow
    if (event === 'terminate' || event === 'reinstate') {
      this.actionSelected.set(event);
    }
    // To show the Transfer or Copy flow
    // this.transferVisible = (event === 'transfer') || (event === 'copy');
    if (event === 'transfer' || event === 'copy') {
      this.transferVisible.set(event);
    }
  }

  selectedMember(member: MemberSelection | MemberSelectionEvent): void {
    if ('value' in member && member.value) {
      this.memberSelected.set(member.value);
      return;
    }

    this.memberSelected.set(member as MemberSelection);
  }

  // Listening to termimnate/reinstate output and clearing the trigger. So, that we can recall it.
  wasTerminatePerformaed(was: boolean): void {
    if (was) {
      this.actionSelected.set('');
    }
  }

  transferOrCopyDone(event: string | { planAcronym: string; clientSpecifiedId: string; subClientSpecifiedId: string }): void {
    if (typeof event !== 'string') {
      this.clientDetails.set(null);
      this.productDetails.set(null);
      this.clientSelected.set({
        planAcronym: event.planAcronym,
        clientSpecifiedId: event.clientSpecifiedId,
        subClientSpecifiedId: event.subClientSpecifiedId
      });
    }
  }

  private buildClientContextKey(clientSelected: ClientSelected | null | undefined): string {
    return JSON.stringify({
      planAcronym: clientSelected?.planAcronym ?? '',
      clientSpecifiedId: clientSelected?.clientSpecifiedId ?? '',
      subClientSpecifiedId: clientSelected?.subClientSpecifiedId ?? '',
      asOf: clientSelected?.asOf ?? ''
    });
  }
}
